export const ARTWORK_QUALITY_WARNING_COPY =
  'Low DPI detected. This artwork may print blurry or pixelated at the selected size. Resize to 300 DPI for the best result, or continue and print at your own risk.'

export const OVERSIZED_ARTWORK_WARNING_COPY =
  'This artwork is larger than the selected sheet. Reduce the width or choose a larger sheet.'

export const SHEET_COVERAGE_WARNING_COPY =
  'There is plenty of open space on this sheet. Add more copies or artwork if you want to use more of it.'

export const LOW_SHEET_COVERAGE_THRESHOLD = 0.15
export const TARGET_ARTWORK_DPI = 300
export const MINIMUM_ARTWORK_DPI = 150

export function calculateDpi(widthPx, heightPx, printWidthIn, printHeightIn) {
  const width = Number(widthPx)
  const height = Number(heightPx)
  const printWidth = Number(printWidthIn)
  const printHeight = Number(printHeightIn)

  if (!width || !height || !printWidth || !printHeight) return null
  if (width <= 0 || height <= 0 || printWidth <= 0 || printHeight <= 0) return null

  return Math.round(Math.min(width / printWidth, height / printHeight))
}

export function isArtworkOversized(printWidthIn, printHeightIn, sheetSize) {
  const printWidth = Number(printWidthIn)
  const printHeight = Number(printHeightIn)
  const sheetWidth = Number(sheetSize?.width_in)
  const sheetHeight = Number(sheetSize?.height_in)

  if (!printWidth || !printHeight || !sheetWidth || !sheetHeight) return false

  return printWidth > sheetWidth || printHeight > sheetHeight
}

export function getOversizedArtworkWarningCopy(printWidthIn, printHeightIn, sheetSize) {
  const printWidth = Number(printWidthIn)
  const printHeight = Number(printHeightIn)
  const sheetWidth = Number(sheetSize?.width_in)
  const sheetHeight = Number(sheetSize?.height_in)

  if (printWidth > sheetWidth && printHeight > sheetHeight) {
    return 'This artwork is larger than the selected sheet. Reduce the width and height or choose a larger sheet.'
  }
  if (printHeight > sheetHeight) {
    return 'This artwork is larger than the selected sheet. Reduce the height or choose a larger sheet.'
  }
  return OVERSIZED_ARTWORK_WARNING_COPY
}

export function getDpiQuality(dpi) {
  if (dpi === null || dpi === undefined || Number.isNaN(dpi)) return null
  if (dpi >= TARGET_ARTWORK_DPI) return 'good'
  if (dpi >= MINIMUM_ARTWORK_DPI) return 'caution'
  return 'poor'
}

export function recommendedPrintDimensions(widthPx, heightPx, targetDpi = TARGET_ARTWORK_DPI) {
  const width = Number(widthPx)
  const height = Number(heightPx)
  const dpi = Number(targetDpi)

  if (!width || !height || !dpi || width <= 0 || height <= 0 || dpi <= 0) return null

  // Inputs only support two decimal places. Rounding a dimension up can make
  // the recommended size fall just below the target DPI (for example, 299).
  const safeHundredths = value => Math.floor(value * 100) / 100

  return {
    widthIn: safeHundredths(width / dpi),
    heightIn: safeHundredths(height / dpi),
  }
}

export function buildOversizedArtworkWarnings(layout, images) {
  const imageById = new Map((images || []).map(image => [String(image.id), image]))

  return (layout?.sheets || []).flatMap((sheet, sheetIndex) =>
    (sheet.items || []).flatMap((item, itemIndex) => {
      if (!isArtworkOversized(item.width_in, item.height_in, sheet)) return []

      const imageId = item.uploaded_image_id ?? item.uploadedImageId
      const image = imageById.get(String(imageId))

      return [{
        image,
        imageName: image?.originalFilename || `Image ${imageId}`,
        sheetNumber: sheet.sheet_number ?? sheetIndex + 1,
        itemIndex,
        widthIn: item.width_in,
        heightIn: item.height_in,
      }]
    })
  )
}

export function buildArtworkQualityWarnings(layout, images) {
  const imageById = new Map((images || []).map(image => [String(image.id), image]))

  return (layout?.sheets || []).flatMap((sheet, sheetIndex) =>
    (sheet.items || []).flatMap((item, itemIndex) => {
      if (isArtworkOversized(item.width_in, item.height_in, sheet)) return []

      const image = imageById.get(String(item.uploaded_image_id ?? item.uploadedImageId))
      const dpi = calculateDpi(image?.widthPx, image?.heightPx, item.width_in, item.height_in)
      const quality = getDpiQuality(dpi)

      if (quality !== 'caution' && quality !== 'poor') return []

      return [{
        dpi,
        quality,
        image,
        imageName: image?.originalFilename || `Image ${item.uploaded_image_id ?? item.uploadedImageId}`,
        sheetNumber: sheet.sheet_number ?? sheetIndex + 1,
        itemIndex,
      }]
    })
  )
}

export function calculateSheetCoverage(layout) {
  const sheets = layout?.sheets || []
  let usedArtworkArea = 0
  let sheetArea = 0

  sheets.forEach(sheet => {
    const sheetWidth = Number(sheet.width_in)
    const sheetHeight = Number(sheet.height_in)
    const items = sheet.items || []

    if (sheetWidth > 0 && sheetHeight > 0) {
      sheetArea += sheetWidth * sheetHeight
    }

    items.forEach(item => {
      const itemWidth = Number(item.width_in)
      const itemHeight = Number(item.height_in)

      if (itemWidth > 0 && itemHeight > 0) {
        usedArtworkArea += itemWidth * itemHeight
      }
    })
  })

  if (sheetArea <= 0) return null

  return usedArtworkArea / sheetArea
}

export function isSheetMostlyEmpty(layout, threshold = LOW_SHEET_COVERAGE_THRESHOLD) {
  const coverage = calculateSheetCoverage(layout)
  if (coverage === null) return false

  return coverage < threshold
}
